/**
 * Niche Finder — utilitarios compartilhados.
 *
 * Formatadores, flags de idioma, helpers de avatar etc.
 * Cores e icones de classificacao usam Lucide via referencias de
 * componente (consumidor renderiza com <Icon size=... />).
 */
import {
  Flame, Rocket, TrendingUp, Sparkles, Activity, Gem, Sprout,
  DollarSign, Trophy,
} from 'lucide-react';

// ============================================================
// Idiomas: ISO 639-1 / BCP 47 -> emoji bandeira
// ============================================================
export const LANGUAGE_FLAGS = {
  'pt-BR': '🇧🇷', 'pt-PT': '🇵🇹', 'pt': '🇧🇷',
  'en': '🇺🇸', 'en-US': '🇺🇸', 'en-GB': '🇬🇧',
  'es': '🇪🇸', 'es-ES': '🇪🇸', 'es-MX': '🇲🇽',
  'de': '🇩🇪',
  'fr': '🇫🇷',
  'it': '🇮🇹',
  'nl': '🇳🇱',
  'ru': '🇷🇺',
  'tr': '🇹🇷',
  'ar': '🇸🇦',
  'hi': '🇮🇳',
  'ja': '🇯🇵',
  'ko': '🇰🇷',
  'zh': '🇨🇳',
  'id': '🇮🇩',
  'tl': '🇵🇭',
  'th': '🇹🇭',
  'vi': '🇻🇳',
  'pl': '🇵🇱',
  'sv': '🇸🇪',
  'no': '🇳🇴',
  'da': '🇩🇰',
  'fi': '🇫🇮',
  'cs': '🇨🇿',
  'sk': '🇸🇰',
  'el': '🇬🇷',
  'he': '🇮🇱',
  'uk': '🇺🇦',
  'bg': '🇧🇬',
  'sr': '🇷🇸',
  'sl': '🇸🇮',
  'hr': '🇭🇷',
  'hu': '🇭🇺',
  'ro': '🇷🇴',
  'ca': '🇪🇸',
  'et': '🇪🇪',
  'lv': '🇱🇻',
  'cy': '🇬🇧',
  'so': '🇸🇴',
  'sw': '🇰🇪',
};

export function getLanguageFlag(iso) {
  if (!iso) return '🌐';
  return LANGUAGE_FLAGS[iso] || '🌐';
}

// ============================================================
// ISO 639 (language) -> ISO 3166-1 alpha-2 (country)
// Usado com <FlagIcon code="br" /> da lib flag-icons.
// 'xx' sinaliza fallback (icone globo no FlagIcon).
//
// Chaves SEMPRE em lowercase — a busca normaliza o input pra
// lowercase tambem. Isso evita o bug onde 'PT-BR' (vindo do
// backend com case inesperado) nao casava com 'pt-BR' literal
// da tabela e caia em 'xx' -> globo.
// ============================================================
const LANG_TO_COUNTRY = {
  'pt-br': 'br', 'pt-pt': 'pt', 'pt': 'br',
  'en': 'us', 'en-us': 'us', 'en-gb': 'gb',
  'es': 'es', 'es-es': 'es', 'es-mx': 'mx',
  'de': 'de',
  'fr': 'fr', 'fr-fr': 'fr', 'fr-ca': 'ca',
  'it': 'it',
  'ja': 'jp',
  'ko': 'kr',
  'zh': 'cn', 'zh-cn': 'cn', 'zh-tw': 'tw',
  'ru': 'ru',
  'ar': 'sa',
  'hi': 'in',
  'tr': 'tr',
  'nl': 'nl',
  'pl': 'pl',
  'sv': 'se',
  'id': 'id',
  'tl': 'ph',
  'th': 'th',
  'vi': 'vn',
  'no': 'no',
  'da': 'dk',
  'fi': 'fi',
  'cs': 'cz',
  'sk': 'sk',
  'el': 'gr',
  'he': 'il',
  'uk': 'ua',
  'bg': 'bg',
  'sr': 'rs',
  'sl': 'si',
  'hr': 'hr',
  'hu': 'hu',
  'ro': 'ro',
  'ca': 'es',
  'et': 'ee',
  'lv': 'lv',
  'cy': 'gb',
  'so': 'so',
  'sw': 'ke',
};

export function getCountryCode(iso) {
  const normalized = String(iso || '').trim().toLowerCase();
  if (!normalized) return 'xx';
  return LANG_TO_COUNTRY[normalized] || 'xx';
}

/** Recebe ISO + t() do i18next e devolve nome traduzido.
 *  Fallback pro proprio codigo se a chave nao existir. */
export function getLanguageName(iso, t) {
  if (!iso) return t('languages.unknown');
  const key = `languages.${iso}`;
  const translated = t(key);
  // i18next devolve a key literal quando nao acha traducao.
  return translated === key ? iso : translated;
}

/** Devolve "Portugues BR", "Espanhol", "Ingles UK", etc — combina o
 *  nome traduzido do idioma com um sufixo de pais quando o codigo tem
 *  variante regional util (pt-BR vs pt-PT, en vs en-GB, es vs es-MX...).
 *  Case-insensitive. */
export function getLanguageShortLabel(iso, t) {
  if (!iso) return t('languages.unknown');
  const normalized = String(iso).trim().toLowerCase();
  if (!normalized) return t('languages.unknown');

  // Variantes com pais relevante — exibe sufixo curto.
  // Usa i18n com fallback pro codigo se a chave nao existir.
  const tx = (key, fallback) => {
    const v = t(key);
    return v === key ? fallback : v;
  };

  // Idioma BASE (languages.pt / .en / .es / ...) + sufixo de pais curto.
  // Usar languages.pt-BR aqui daria "Portugues (BR) BR" — usamos a forma
  // base e anexamos o sufixo do pais.
  if (normalized === 'pt-br' || normalized === 'pt') {
    return `${tx('languages.pt', 'Portugues')} BR`;
  }
  if (normalized === 'pt-pt') return `${tx('languages.pt', 'Portugues')} PT`;
  if (normalized === 'en-gb') return `${tx('languages.en', 'Ingles')} UK`;
  if (normalized === 'en-us') return `${tx('languages.en', 'Ingles')} US`;
  if (normalized === 'es-mx') return `${tx('languages.es', 'Espanhol')} MX`;
  if (normalized === 'es-es') return `${tx('languages.es', 'Espanhol')} ES`;
  if (normalized === 'zh-tw') return `${tx('languages.zh', 'Chines')} TW`;
  if (normalized === 'zh-cn') return `${tx('languages.zh', 'Chines')} CN`;
  if (normalized === 'fr-ca') return `${tx('languages.fr', 'Frances')} CA`;
  if (normalized === 'fr-fr') return `${tx('languages.fr', 'Frances')} FR`;

  // Sem variante de pais util — usa so o nome do idioma (base ISO).
  const baseLang = normalized.split('-')[0];
  return tx(`languages.${baseLang}`, iso);
}

/** Traduz nome de categoria via i18n (viralNiches.categories.X).
 *  Retorna o proprio nome se nao houver traducao registrada. */
export function getCategoryName(category, t) {
  if (!category) return '';
  // Tira emoji prefixado caso venha do banco com "📁 Categoria"
  const clean = String(category).replace(
    /^[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]+\s*/u,
    '',
  );
  if (!clean) return '';
  const key = `viralNiches.categories.${clean}`;
  const translated = t(key, { defaultValue: '' });
  return translated && translated !== key ? String(translated) : clean;
}

// ============================================================
// FONTE UNICA DE COR POR TIER (Pacote 7.2).
// TODOS os componentes (NicheFinderTabs, ChannelCard: tag, top-bar, metricas,
// footer, hover) DEVEM pegar a cor daqui via getTierColor. NAO criar hex de
// cor de tier hardcoded em nenhum outro arquivo. 'all'/'byCategory' = neutro.
// ============================================================
export const TIER_COLORS = {
  exploding:        '#EF4444',  // vermelho
  rising_fast:      '#F59E0B',  // laranja/amber
  high_traction:    '#10B981',  // verde
  strong_potential: '#06B6D4',  // ciano
  recent_traction:  '#8B5CF6',  // roxo
  mature_monetized:      '#84CC16',  // lima (monetizado 3-6m)
  established_monetized: '#EAB308',  // dourado (monetizado 6-12m)
  outlier:          '#EC4899',  // rosa
  new_channels:     '#3B82F6',  // azul
  all:              null,        // neutro (sem cor de tier)
};

export function getTierColor(tierKey) {
  return TIER_COLORS[tierKey] || null;
}

// ============================================================
// Classificacao do canal — 8 TIERS sincronizados com o backend
// (services/nichos_virais_service.py :: _tier_raw_conditions / _tier_where).
// Pacote 5: substitui o set antigo (outlier/rising/growing/new/consolidated)
// pelos 7 tiers reais do backend. Usada pra pintar top bar + tag visual no
// ChannelCard. Retorna null (sem tag) quando o canal nao bate em nenhum tier.
// Pacote 7.2: cor/bg/border/gradient DERIVADOS de TIER_COLORS (fonte unica) —
// zero hex de tier hardcoded aqui.
// ============================================================

// Icones por tier (Lucide). A COR vem sempre de TIER_COLORS.
const TIER_ICONS = {
  exploding:        Flame,
  rising_fast:      Rocket,
  high_traction:    TrendingUp,
  strong_potential: Sparkles,
  recent_traction:  Activity,
  mature_monetized:      DollarSign,
  established_monetized: Trophy,
  outlier:          Gem,
  new_channels:     Sprout,
};

// bg/border usam alpha em hex de 8 digitos (CSS valido); gradient = fade
// horizontal 90deg (cor do tier predominante -> 40% no fim), estilo DarkPlanner.
const CLASSIFICATION_BY_KEY = Object.keys(TIER_ICONS).reduce((acc, key) => {
  const c = TIER_COLORS[key];
  acc[key] = {
    key,
    iconComponent: TIER_ICONS[key],
    color: c,
    bg: `${c}1A`,        // ~10%
    border: `${c}40`,    // ~25%
    gradient: `linear-gradient(90deg, ${c}, ${c}66)`,
  };
  return acc;
}, {});

/** Resolve a classificacao EXCLUSIVA do canal — ESPELHA o _tier_where do
 *  backend (services/nichos_virais_service.py). Cada canal cai em UM tier.
 *  Prioridade decrescente; a PRIMEIRA regra que bate vence (equivalente ao
 *  "AND NOT(tiers de prioridade maior)" do SQL):
 *    1. exploding             fv + <15d  + views > 100k
 *    2. rising_fast           fv + <15d  + 50k <= views <= 100k
 *    3. high_traction         fv + 15-60d + views > 500k
 *    4. strong_potential      fv + 15-60d + 100k <= views <= 500k
 *    5. recent_traction       fv + 61-90d + views > 200k
 *    6. mature_monetized      fv + 91-180d + is_monetized === true
 *    7. established_monetized fv + 181-365d + is_monetized === true
 *    8. outlier               melhor video longo >= 5x a media views/video
 *                             (>= 8 uploads, hit >= 50k; NAO exige first_video_at)
 *    9. new_channels          fv + <30d  + subs < 1k
 *  Retorna null se nao bate em nenhum (canal aparece so na aba "Todos").
 *  `days` (posting age) usa SO first_video_at — igual backend, sem fallback
 *  joined_date. Prefere channel.posting_age_days (vem calculado do backend);
 *  cai pro calculo local de first_video_at se ausente. */
export function getChannelClassification(channel) {
  if (!channel) return null;
  const views = channel.total_views || 0;
  const subs = channel.subscribers || 0;
  const uploads = channel.num_uploads || 0;
  const days = (channel.posting_age_days !== null && channel.posting_age_days !== undefined)
    ? channel.posting_age_days
    : (channel.first_video_at ? daysSince(channel.first_video_at) : null);
  const fv = days !== null && days !== undefined; // tem first_video_at
  // Melhor video LONGO — max sobre top_videos (backend manda ordenado por
  // views DESC, mas max defensivo cobre arrays antigos fora de ordem).
  const topViews = Array.isArray(channel.top_videos)
    ? channel.top_videos.reduce((mx, v) => Math.max(mx, Number(v?.views) || 0), 0)
    : 0;

  // 1. Explodindo
  if (fv && days < 15 && views > 100000) return CLASSIFICATION_BY_KEY.exploding;
  // 2. Subindo Rápido
  if (fv && days < 15 && views >= 50000 && views <= 100000) return CLASSIFICATION_BY_KEY.rising_fast;
  // 3. Alta Tração
  if (fv && days >= 15 && days <= 60 && views > 500000) return CLASSIFICATION_BY_KEY.high_traction;
  // 4. Potencial Forte
  if (fv && days >= 15 && days <= 60 && views >= 100000 && views <= 500000) {
    return CLASSIFICATION_BY_KEY.strong_potential;
  }
  // 5. Tração Recente
  if (fv && days >= 61 && days <= 90 && views > 200000) return CLASSIFICATION_BY_KEY.recent_traction;
  // 6. Monetizado 3-6m — fim do teto de 90d (2026-07-20); so canais com
  //    monetizacao CONFIRMADA pela extensao (true, nunca null).
  if (fv && days >= 91 && days <= 180 && channel.is_monetized === true) {
    return CLASSIFICATION_BY_KEY.mature_monetized;
  }
  // 7. Monetizado 6-12m
  if (fv && days >= 181 && days <= 365 && channel.is_monetized === true) {
    return CLASSIFICATION_BY_KEY.established_monetized;
  }
  // 8. Outlier — melhor video fez >= 5x a media de views/video do canal.
  //    Guards anti-canal-ruim: >= 8 uploads e hit >= 50k views.
  if (uploads >= 8 && views > 0 && topViews >= 50000 && topViews * uploads >= 5 * views) {
    return CLASSIFICATION_BY_KEY.outlier;
  }
  // 9. Canais Novos
  if (fv && days < 30 && subs < 1000) return CLASSIFICATION_BY_KEY.new_channels;

  return null;
}

/** Mapeia classification.key -> chave de i18n do label (tag do card).
 *  Componentes usam: t(getClassificationLabelKey(c.key)). */
export function getClassificationLabelKey(key) {
  switch (key) {
    case 'exploding':             return 'viralNiches.tag_exploding';
    case 'rising_fast':           return 'viralNiches.tag_rising_fast';
    case 'high_traction':         return 'viralNiches.tag_high_traction';
    case 'strong_potential':      return 'viralNiches.tag_strong_potential';
    case 'recent_traction':       return 'viralNiches.tag_recent_traction';
    case 'mature_monetized':      return 'viralNiches.tag_mature_monetized';
    case 'established_monetized': return 'viralNiches.tag_established_monetized';
    case 'outlier':               return 'viralNiches.tag_outlier';
    case 'new_channels':          return 'viralNiches.tag_new_channels';
    default:                      return null;
  }
}

/** Tier (classification.key) -> chave i18n da DESCRICAO da regra (as mesmas
 *  das abas). Usado no tooltip da tag de classificacao do ChannelCard (P7).
 *  Ex: exploding -> "Menos de 15 dias e mais de 100 mil views". */
export const TIER_TOOLTIPS = {
  exploding:        'viralNiches.tab_exploding_desc',
  rising_fast:      'viralNiches.tab_rising_fast_desc',
  high_traction:    'viralNiches.tab_high_traction_desc',
  strong_potential: 'viralNiches.tab_strong_potential_desc',
  recent_traction:  'viralNiches.tab_recent_traction_desc',
  mature_monetized:      'viralNiches.tab_mature_monetized_desc',
  established_monetized: 'viralNiches.tab_established_monetized_desc',
  outlier:          'viralNiches.tab_outlier_desc',
  new_channels:     'viralNiches.tab_new_channels_desc',
};

// ============================================================
// Formatadores de numero
// ============================================================
export function formatK(num) {
  if (num === null || num === undefined) return '—';
  const n = Number(num);
  if (!Number.isFinite(n)) return '—';
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return (n / 1_000_000_000).toFixed(1) + 'B';
  if (abs >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (abs >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return String(n);
}

/** Multiplicador views/subs — sinal de viral.
 *  Pequeno arredondamento decrescente: 0.0-9.9 com 1 casa, 10+ inteiro,
 *  1000+ em k×. */
export function formatMultiplier(views, subs) {
  if (!subs || subs === 0) return '—';
  const m = Number(views) / Number(subs);
  if (!Number.isFinite(m)) return '—';
  if (m >= 1000) return (m / 1000).toFixed(1) + 'k×';
  if (m >= 10) return Math.round(m) + '×';
  return m.toFixed(1) + '×';
}

/** True se multiplicador for "high" (>50) — usado pra colorir verde. */
export function isHighMultiplier(views, subs) {
  if (!subs || subs === 0) return false;
  return Number(views) / Number(subs) > 50;
}

// ============================================================
// Datas
// ============================================================

/** Tempo relativo curto pro footer dos cards ("ha 3 dias", "agora").
 *  Usa as i18n keys viralNiches.time_now/min/hour/day/week/month/year.
 *  Retorna null se a data eh invalida. */
export function formatRelativeTime(isoDate, t) {
  if (!isoDate) return null;
  const past = new Date(isoDate).getTime();
  if (!Number.isFinite(past)) return null;
  const diffMs = Date.now() - past;
  if (diffMs < 0) return t('viralNiches.time_now');

  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return t('viralNiches.time_now');
  if (diffMin < 60) return t('viralNiches.time_min', { n: diffMin });

  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return t('viralNiches.time_hour', { n: diffHour });

  const diffDay = Math.floor(diffHour / 24);
  if (diffDay < 7) return t('viralNiches.time_day', { n: diffDay });

  const diffWeek = Math.floor(diffDay / 7);
  if (diffWeek < 5) return t('viralNiches.time_week', { n: diffWeek });

  const diffMonth = Math.floor(diffDay / 30);
  if (diffMonth < 12) return t('viralNiches.time_month', { n: diffMonth });

  const diffYear = Math.floor(diffDay / 365);
  return t('viralNiches.time_year', { n: diffYear });
}

/** Data absoluta formatada (PT-BR style com hora) — usada em tooltips
 *  pra mostrar o timestamp exato. Retorna null se invalida. */
export function formatAbsoluteDate(isoDate) {
  if (!isoDate) return null;
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return null;
  try {
    return d.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return d.toISOString();
  }
}

/**
 * Pacote 8C/8D — "Qual a idade dos dados?" pra UX de freshness no card.
 * Retorna { label, isStale } ou null se nao deve ser exibido.
 *
 *  - < 5min      -> { label: "atualizado agora",         isStale: false } (8D)
 *  - 5-59min     -> { label: "atualizado ha 12min",      isStale: false } (8D)
 *  - 1-23h       -> { label: "atualizado ha 12h",        isStale: false }
 *  - 1-7 dias    -> { label: "atualizado ha 3 dias",     isStale: false }
 *  - > 7 dias    -> { label: "dados de 15 dias atras",   isStale: true  }
 *
 * Pacote 8D: regra anterior escondia tudo <1h (dado fresco) — usuario que
 * acabou de visitar canal via extensao perdia a confirmacao visual de refresh.
 * Agora SEMPRE exibe, com granularidade fina pra recem-atualizado.
 *
 * NAO confundir com formatRelativeTime acima — esta funcao tem semantica
 * de "freshness" (com flag isStale, thresholds proprios e i18n especifico
 * de updated/data_age), nao de "X tempo atras" generico.
 */
export function formatFreshness(timestamp, t) {
  if (!timestamp) return null;
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return null;

  const diffMs = Date.now() - date.getTime();
  if (diffMs < 0) return null;  // futuro = ignora (defensivo)

  const diffMin = Math.floor(diffMs / (1000 * 60));
  const diffH = Math.floor(diffMin / 60);
  const diffD = Math.floor(diffH / 24);

  // < 5min: "atualizado agora" (recem-refrescado pela extensao).
  if (diffMin < 5) {
    return {
      label: t('viralNiches.updated_now'),
      isStale: false,
    };
  }
  // 5-59min: granularidade em minutos pra ainda comunicar frescor.
  if (diffMin < 60) {
    return {
      label: t('viralNiches.updated_ago_minutes', { minutes: diffMin }),
      isStale: false,
    };
  }
  // 1-23h
  if (diffD < 1) {
    return {
      label: t('viralNiches.updated_ago_hours', { hours: diffH }),
      isStale: false,
    };
  }
  // 1-7 dias
  if (diffD <= 7) {
    return {
      label: t('viralNiches.updated_ago_days', { days: diffD }),
      isStale: false,
    };
  }
  // > 7 dias: dado velho — flag isStale aciona cor laranja no card.
  return {
    label: t('viralNiches.data_age_days', { days: diffD }),
    isStale: true,
  };
}

export function daysSince(isoDate) {
  if (!isoDate) return null;
  const past = new Date(isoDate);
  if (Number.isNaN(past.getTime())) return null;
  const now = new Date();
  return Math.floor((now - past) / (1000 * 60 * 60 * 24));
}

/**
 * Formata um número de DIAS em texto amigável de tempo postando (Pacote 4):
 *   < 30 dias   -> "X dias postando"
 *   < 12 meses  -> "Y meses postando"
 *   >= 12 meses -> "Z anos [e W meses] postando"
 * Retorna null se days for null/negativo.
 * Nota: hardcoded PT (app pt-BR primary). Se precisar EN/ES, migrar pra t().
 */
export function formatPostingTime(days) {
  if (days == null || days < 0) return null;
  if (days < 30) {
    return `${days} dia${days !== 1 ? 's' : ''} postando`;
  }
  const totalMonths = Math.floor(days / 30);
  if (totalMonths < 12) {
    return `${totalMonths} ${totalMonths === 1 ? 'mês' : 'meses'} postando`;
  }
  const years = Math.floor(totalMonths / 12);
  const remainingMonths = totalMonths % 12;
  if (remainingMonths === 0) {
    return `${years} ${years === 1 ? 'ano' : 'anos'} postando`;
  }
  return `${years} ${years === 1 ? 'ano' : 'anos'} e ${remainingMonths} ${remainingMonths === 1 ? 'mês' : 'meses'} postando`;
}

/**
 * Versao ABREVIADA do tempo postando (Pacote 7.2): '14d', '3m', '1a', '1a 2m'.
 * Usada no footer compacto do ChannelCard. Retorna null se days invalido.
 */
export function formatPostingTimeShort(days) {
  if (days == null || days < 0) return null;
  if (days < 30) return `${days}d`;
  const totalMonths = Math.floor(days / 30);
  if (totalMonths < 12) return `${totalMonths}m`;
  const years = Math.floor(totalMonths / 12);
  const remainingMonths = totalMonths % 12;
  if (remainingMonths === 0) return `${years}a`;
  return `${years}a ${remainingMonths}m`;
}

/** Uploads por mes baseado em num_uploads + first_video_at. */
export function calculateUploadsPerMonth(numUploads, firstVideoAt) {
  if (!firstVideoAt || !numUploads) return null;
  const days = daysSince(firstVideoAt);
  if (!days || days < 1) return null;
  const months = days / 30.44;
  const rate = numUploads / months;
  if (rate >= 1) return rate.toFixed(1);
  return rate.toFixed(2);
}

// ============================================================
// Avatar fallback
// ============================================================
export function getInitials(title) {
  if (!title) return '?';
  const words = String(title).trim().split(/\s+/).slice(0, 2);
  const initials = words.map((w) => w[0]?.toUpperCase() || '').join('');
  return initials.slice(0, 2) || '?';
}

// Cores estaveis pra avatar — deterministicas via hash do channel_id
const AVATAR_COLORS = [
  '#0DAEFF', '#534AB7', '#34D399', '#FB923C',
  '#D4537E', '#FBBF24', '#A78BFA', '#1D9E75',
];

export function getAvatarColor(channelId) {
  if (!channelId) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < channelId.length; i++) {
    hash = (hash << 5) - hash + channelId.charCodeAt(i);
    hash = hash & hash; // forca 32-bit
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

// ============================================================
// Visited channels (localStorage persistence)
// ============================================================
const VISITED_KEY = 'niche_finder_visited_channels';

export function getVisitedChannels() {
  try {
    const raw = localStorage.getItem(VISITED_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function markChannelAsVisited(channelId) {
  try {
    const list = getVisitedChannels();
    if (!list.includes(channelId)) {
      list.push(channelId);
      localStorage.setItem(VISITED_KEY, JSON.stringify(list));
    }
    return list;
  } catch {
    return [];
  }
}

export function isChannelVisited(channelId, list) {
  return Array.isArray(list) && list.includes(channelId);
}

// ============================================================
// Faixas de inscritos pra filtro (atalhos rapidos + 'custom')
// ============================================================
export const SUBS_RANGES = [
  { value: 'all',     min: null,      max: null      },
  { value: '0_1k',    min: 0,         max: 1_000     },
  { value: '1k_10k',  min: 1_000,     max: 10_000    },
  { value: '10k_100k',min: 10_000,    max: 100_000   },
  { value: '100k_1m', min: 100_000,   max: 1_000_000 },
  { value: '1m_plus', min: 1_000_000, max: null      },
  { value: 'custom',  min: null,      max: null      },
];

// ============================================================
// Faixas de Nº de VÍDEOS pra filtro (mesma estrutura de SUBS_RANGES).
// Caso de uso nº 1: achar canais com POUCOS vídeos e muitas views.
// ============================================================
export const VIDEOS_RANGES = [
  { value: 'all',      min: null, max: null },
  { value: '0_10',     min: 0,    max: 10   },
  { value: '10_50',    min: 10,   max: 50   },
  { value: '50_100',   min: 50,   max: 100  },
  { value: '100_500',  min: 100,  max: 500  },
  { value: '500_plus', min: 500,  max: null },
  { value: 'custom',   min: null, max: null },
];

// ============================================================
// Faixas de Views pra filtro (mesma estrutura de SUBS_RANGES)
// ============================================================
export const VIEWS_RANGES = [
  { value: 'all',        min: null,         max: null         },
  { value: '0_100k',     min: 0,            max: 100_000      },
  { value: '100k_1m',    min: 100_000,      max: 1_000_000    },
  { value: '1m_10m',     min: 1_000_000,    max: 10_000_000   },
  { value: '10m_100m',   min: 10_000_000,   max: 100_000_000  },
  { value: '100m_plus',  min: 100_000_000,  max: null         },
  { value: 'custom',     min: null,         max: null         },
];

function _findRange(list, value) {
  return list.find((r) => r.value === value);
}

/** Aplica faixa pre-definida OU min/max custom.
 *  rangeValue: 'all' | 'custom' | '1k_10k' | etc.
 *  customMin / customMax: numbers (usados quando rangeValue === 'custom'). */
export function channelMatchesRange(value, rangeValue, customMin, customMax, rangeList) {
  if (!rangeValue || rangeValue === 'all') return true;
  const v = Number(value) || 0;
  if (rangeValue === 'custom') {
    if (customMin !== null && customMin !== undefined && !Number.isNaN(customMin) && v < customMin) return false;
    if (customMax !== null && customMax !== undefined && !Number.isNaN(customMax) && v >= customMax) return false;
    return true;
  }
  const range = _findRange(rangeList, rangeValue);
  if (!range) return true;
  if (range.min !== null && v < range.min) return false;
  if (range.max !== null && v >= range.max) return false;
  return true;
}

export function channelMatchesSubsRange(subs, rangeValue, min, max) {
  return channelMatchesRange(subs, rangeValue, min, max, SUBS_RANGES);
}

export function channelMatchesViewsRange(views, rangeValue, min, max) {
  return channelMatchesRange(views, rangeValue, min, max, VIEWS_RANGES);
}

/** Parser de input livre: "1000" -> 1000, "10k" -> 10000, "1.5M" -> 1_500_000.
 *  Retorna null se a string nao casar com formato esperado. */
export function parseSubsInput(input) {
  if (input === null || input === undefined) return null;
  const raw = String(input).trim().toLowerCase().replace(/[,\s]/g, '');
  if (!raw) return null;
  // Aceita "1000", "1.5", "10k", "10K", "1.5m", "1m", "1b"
  const match = raw.match(/^(\d+(?:\.\d+)?)\s*([kmb]?)$/);
  if (!match) return null;
  const n = parseFloat(match[1]);
  if (!Number.isFinite(n)) return null;
  const suffix = match[2];
  if (suffix === 'k') return Math.round(n * 1_000);
  if (suffix === 'm') return Math.round(n * 1_000_000);
  if (suffix === 'b') return Math.round(n * 1_000_000_000);
  return Math.round(n);
}

// ============================================================
// Busca inteligente — concat de campos searcheaveis do canal
// ============================================================
export function buildChannelSearchableText(channel, helpers = {}) {
  if (!channel) return '';
  const { getLanguageNameFn, getCategoryNameFn } = helpers;
  const cats = Array.isArray(channel.categories) ? channel.categories : [];
  const secondary = Array.isArray(channel.secondary_categories) ? channel.secondary_categories : [];
  const keywords = Array.isArray(channel.keywords) ? channel.keywords : [];
  const parts = [
    channel.title,
    channel.channel_url,
    channel.description,
    channel.primary_category,
    channel.subcategory,
    ...cats,
    ...secondary,
    ...keywords,
    channel.country,
    channel.language_detected,
  ];
  // Idioma traduzido (ex: 'Português' / 'Portuguese' / 'Portugués')
  if (typeof getLanguageNameFn === 'function' && channel.language_detected) {
    parts.push(getLanguageNameFn(channel.language_detected));
  }
  // Categorias traduzidas — permite busca por "história" achar "History"
  if (typeof getCategoryNameFn === 'function') {
    if (channel.primary_category) parts.push(getCategoryNameFn(channel.primary_category));
    cats.forEach((c) => parts.push(getCategoryNameFn(c)));
    secondary.forEach((c) => parts.push(getCategoryNameFn(c)));
  }
  return parts
    .filter(Boolean)
    .map((s) => String(s).toLowerCase())
    .join(' ');
}

/** Busca inteligente: termo dividido em palavras, todas devem estar
 *  presentes no texto searcheavel (AND simples).
 *
 *  `helpers` = { getLanguageNameFn, getCategoryNameFn }
 *  (ambos opcionais; sem eles, nao traduz idioma/categoria). */
export function filterChannelsBySearch(channels, searchTerm, helpers = {}) {
  const term = String(searchTerm || '').toLowerCase().trim();
  if (!term) return channels;
  const words = term.split(/\s+/).filter(Boolean);
  if (words.length === 0) return channels;
  return channels.filter((c) => {
    const haystack = buildChannelSearchableText(c, helpers);
    return words.every((w) => haystack.includes(w));
  });
}

// ============================================================
// Thumbnail helper — fallback pra hqdefault.jpg quando precisa
// ============================================================
export function getVideoThumbnail(videoId, override) {
  if (override) return override;
  if (!videoId) return null;
  return `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
}
